import React, { useCallback } from 'react';
import { WalteroChart } from '../waltero';
import ExportButton from '../ExportButton';
import { exportTableData } from '../../utils/csvExport';
import { formatConsumptionValue } from '../../utils/reportFormatters';
import { useReportData } from '../../context/ReportDataContext';

const DailyConsumption = () => {
  const { reportData } = useReportData();

  const handleExportDailyConsumption = useCallback(() => {
    // Get all unique dates from all devices
    const allDates = new Set();
    reportData.forEach(device => {
      if (device.dailyUsage) {
        device.dailyUsage.forEach(day => {
          allDates.add(day.date);
        });
      }
    });
    
    const sortedDates = Array.from(allDates).sort().reverse();
    exportTableData('daily-consumption', { reportData, sortedDates });
  }, [reportData]);

  // Get all unique dates from all devices
  const allDates = new Set();
  reportData.forEach(device => {
    if (device.dailyUsage) {
      device.dailyUsage.forEach(day => {
        allDates.add(day.date);
      });
    }
  });

  const sortedDates = Array.from(allDates).sort().reverse();

  if (sortedDates.length === 0) {
    return (
      <div className="daily-consumption-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>Daily Fleet Consumption</h3>
          <ExportButton 
            onClick={handleExportDailyConsumption}
            disabled={true}
            tooltip="Export daily consumption data to CSV"
          />
        </div>
        <p>No daily consumption data available</p>
      </div>
    );
  }

  // Calculate total consumption for each date for the chart
  const totalConsumptionByDate = sortedDates.map(date => {
    const totalForDate = reportData.reduce((sum, device) => {
      const dayData = device.dailyUsage?.find(day => day.date === date);
      return sum + (dayData ? dayData.consumption : 0);
    }, 0);
    return { date, total: totalForDate };
  });

  // Prepare data for ECharts
  const chartData = totalConsumptionByDate.map(dayData => ({
    date: new Date(dayData.date).toLocaleDateString(),
    consumption: dayData.total
  }));

  const chartOption = {
    title: {
      text: 'Total Daily Consumption',
      left: 'left',
      textStyle: {
        color: '#023861',
        fontSize: 16,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const data = params[0];
        return `${data.name}<br/>Consumption: ${data.value.toFixed(1)}`;
      }
    },
    xAxis: {
      type: 'category',
      data: chartData.map(item => item.date),
      axisLabel: {
        color: '#094872',
        fontSize: 12,
        rotate: 45
      },
      axisLine: {
        lineStyle: {
          color: '#d7e2e4'
        }
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#094872',
        fontSize: 12,
        formatter: '{value}'
      },
      axisLine: {
        lineStyle: {
          color: '#d7e2e4'
        }
      },
      splitLine: {
        lineStyle: {
          color: '#f0f0f0',
          type: 'dashed'
        }
      }
    },
    series: [{
      name: 'Daily Consumption',
      type: 'bar',
      data: chartData.map(item => item.consumption),
      itemStyle: {
        color: '#3d8dae',
        borderRadius: [4, 4, 0, 0]
      },
      emphasis: {
        itemStyle: {
          color: '#23678b'
        }
      },
      barWidth: '60%'
    }],
    grid: {
      left: '10%',
      right: '5%',
      bottom: '15%',
      top: '15%',
      containLabel: true
    }
  };

  return (
    <div className="daily-consumption-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3>Daily Fleet Consumption</h3>
        <ExportButton 
          onClick={handleExportDailyConsumption}
          disabled={reportData.length === 0}
          tooltip="Export daily consumption data to CSV"
        />
      </div>
      
      {/* Total Consumption Bar Graph */}
      <div className="total-consumption-chart">
        <div className="total-consumption-bars">
          <WalteroChart
            option={chartOption}
            height={350}
            style={{ width: '100%' }}
            aria-label="Daily consumption bar chart showing total usage by date"
            role="img"
            data-testid="daily-consumption-chart"
          />
        </div>
      </div>
      
      {/* Daily Consumption Table */}
      <div className="daily-consumption-table">
        <table className="report-table">
          <thead>
            <tr>
              <th>Device</th>
              <th style={{ textAlign: 'right' }}>Total Usage</th>
              {sortedDates.map(date => (
                <th key={date}>{new Date(date).toLocaleDateString()}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reportData.map(device => {
              // Calculate total usage for this device over the selected period
              const totalUsage = device.dailyUsage?.reduce((sum, day) => sum + (day.consumption || 0), 0) || 0;
              
              return (
                <tr key={device.deviceId}>
                  <td>{device.serialNumber}</td>
                  <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                    {formatConsumptionValue(totalUsage)}
                  </td>
                  {sortedDates.map(date => {
                    const dayData = device.dailyUsage?.find(day => day.date === date);
                    return (
                      <td key={date} className={dayData ? 'has-data' : 'no-data'} style={{ textAlign: 'right' }}>
                        {dayData ? formatConsumptionValue(dayData.consumption) : 'N/A'}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {/* Total row */}
            <tr className="total-row">
              <td><strong>Total</strong></td>
              <td style={{ textAlign: 'right' }}>
                <strong>
                  {formatConsumptionValue(
                    reportData.reduce((sum, device) => {
                      const deviceTotal = device.dailyUsage?.reduce((deviceSum, day) => deviceSum + (day.consumption || 0), 0) || 0;
                      return sum + deviceTotal;
                    }, 0)
                  )}
                </strong>
              </td>
              {sortedDates.map(date => {
                const totalForDate = reportData.reduce((sum, device) => {
                  const dayData = device.dailyUsage?.find(day => day.date === date);
                  return sum + (dayData ? dayData.consumption : 0);
                }, 0);
                return (
                  <td key={date} className="total-cell" style={{ textAlign: 'right' }}>
                    <strong>{formatConsumptionValue(totalForDate)}</strong>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DailyConsumption;